import { inject, Injectable, Provider, signal } from '@angular/core';
import { NavigationEnd, Route, Router } from '@angular/router';
import { AppDefinitionService } from '@processpuzzle/base-app';
import { APPLICATION_CONTEXT, ApplicationContext, NavMenuItem } from '@processpuzzle/base-widget';
import { ORG_ADMIN_ORG_KEY } from '@processpuzzle/org-admin';
import { catchError, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { currentApplicationOf, navigableRoutes, toApplicationList } from './current-application';

/**
 * This application's answer to base-widget's `APPLICATION_CONTEXT`: the customer's application
 * name and the titled routes, for `app-title` and `nav-menu` to read without props.
 *
 * **Reads `router.config` rather than importing a routes constant.** This application's routes are
 * built at bootstrap from the organization the URL named — see `createAppRoutes` — so there is no
 * module-level array to import, and importing one would offer navigation for an application that
 * was started for a different tenant.
 *
 * The name is the seeded starter app's, which is named for the customer organization. It is
 * fetched once the first navigation has completed, so the request always carries the session the
 * protected route's login guard established.
 *
 * **Lists the tenant's applications rather than reading `app` by id.** The seeded one is called
 * `app`, but a tenant may have deleted it and kept only an installed starter's (`sailrc`), and
 * `centralHttpErrorInterceptor` reports every failed request — a by-id read would put a 404 in the
 * log and in front of the customer on every page load. Which one names the shell is
 * `currentApplicationOf`'s rule, the runtime host's too; a tenant with none shows no name.
 *
 * Provided at the root, for the workspace chrome — designer and administration. The application
 * itself, in the runtime host, and the designer's Application preview both render a base-app
 * `AppShellComponent`, which provides its own context for the definition it renders, so widgets
 * there see that application rather than this one.
 */
@Injectable()
export class CustomApplicationContext implements ApplicationContext {
  private readonly router = inject(Router);
  private readonly appDefinitionService = inject(AppDefinitionService);
  private readonly hasOrganization = inject(ORG_ADMIN_ORG_KEY) !== '';
  private readonly appName = signal('');

  readonly name = this.appName.asReadonly();
  readonly logoUrl = signal<string | undefined>(undefined).asReadonly();
  /** Only titled routes appear in navigation; the auth matcher, the redirects and `**` do not. */
  readonly navItems = signal(navigableRoutes(this.router.config).map(toNavMenuItem)).asReadonly();

  constructor() {
    if (!this.hasOrganization) return;
    const firstNavigation$: Observable<unknown> = this.router.navigated
      ? of(undefined)
      : this.router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          take(1),
        );
    firstNavigation$
      .pipe(
        switchMap(() => this.appDefinitionService.findAll()),
        map((response) => currentApplicationOf(toApplicationList(response))),
        // An empty collection arrives as an error, not as `[]`; HTTP failures were reported by the interceptor.
        catchError(() => of(undefined)),
      )
      .subscribe((definition) => this.appName.set(definition?.name ?? ''));
  }
}

export function provideCustomApplicationContext(): Provider[] {
  return [{ provide: APPLICATION_CONTEXT, useClass: CustomApplicationContext }];
}

function toNavMenuItem(route: Route): NavMenuItem {
  const menuTitle = (route.data?.['menuTitle'] as string) ?? '';
  const icon = route.data?.['icon'] as string | undefined;
  // Absolute: a bare `acme/admin` resolves against the current route and produces
  // `/acme/admin/organization-user/acme/admin` from inside the admin branch.
  const routePath = `/${route.path ?? ''}`;
  return { id: routePath, label: menuTitle, translocoId: `navigation.${menuTitle}`, ...(icon ? { icon } : {}), routePath };
}
